import { z } from 'zod'

import { BLOCK_TYPES, type BlockType } from './blocks'
import { IdSchema } from './common'

// Public-site templates (issue #75). A template decides which page blocks the editor offers and which header and
// footer the site wears. "Editorial" is the original design and offers everything; "Landing" is a compact
// one-page style for sites that only need a few sections; "Boutique" is a white, product-first retail style
// (jewelry, fashion, luxury) with its own header, footer and catalogue pages; "Studio" is a bright, rounded,
// playful style for a game studio (orange and blue, a sunburst hero, a game panel with store buttons).

export const TEMPLATE_IDS = ['editorial', 'landing', 'boutique', 'studio'] as const
export const TemplateIdSchema = z.enum(TEMPLATE_IDS)
export type TemplateId = z.infer<typeof TemplateIdSchema>
export const DEFAULT_TEMPLATE_ID: TemplateId = 'editorial'

export interface SiteTemplate {
  id: TemplateId
  /**
   * `full` keeps the mega menu, dropdowns and the big footer; `simple` is a logo, a flat link row and a call to action;
   * `boutique` is an announcement bar, a centred logo over a centred menu, and a black four-column footer;
   * `studio` is a logo with a "By Digimasi" line, flat links and a round contact button, over a light footer.
   */
  header: 'full' | 'simple' | 'boutique' | 'studio'
  footer: 'full' | 'simple' | 'boutique' | 'studio'
  /** Category and product pages: the long-form `packaging` landing page, or the `boutique` product grid and detail page. */
  productPages: 'packaging' | 'boutique'
  /** Page blocks the editor offers; the whole set for a template that lists none. */
  blocks: readonly BlockType[] | 'all'
}

export const SITE_TEMPLATES: Record<TemplateId, SiteTemplate> = {
  editorial: { id: 'editorial', header: 'full', footer: 'full', productPages: 'packaging', blocks: 'all' },
  landing: {
    id: 'landing',
    header: 'simple',
    footer: 'simple',
    productPages: 'packaging',
    blocks: ['hero', 'split', 'stats', 'cardGrid', 'content', 'faq', 'quotes', 'imageGrid', 'columns', 'cta', 'booking'],
  },
  boutique: {
    id: 'boutique',
    header: 'boutique',
    footer: 'boutique',
    productPages: 'boutique',
    blocks: ['hero', 'split', 'stats', 'cardGrid', 'content', 'faq', 'quotes', 'imageGrid', 'catalogue', 'columns', 'cta', 'strip', 'showcase', 'spotlight'],
  },
  // Category and product pages stay the packaging ones: a game studio has no catalogue.
  studio: {
    id: 'studio',
    header: 'studio',
    footer: 'studio',
    productPages: 'packaging',
    blocks: ['hero', 'stats', 'game', 'imageGrid', 'cardGrid', 'faq', 'cta', 'content', 'columns', 'quotes'],
  },
}

export const templateOf = (id: string | null | undefined): SiteTemplate => SITE_TEMPLATES[TemplateIdSchema.safeParse(id).data ?? DEFAULT_TEMPLATE_ID]

/** Whether the template's editor offers a block type. */
export const templateAllowsBlock = (id: string | null | undefined, type: BlockType): boolean => {
  const { blocks } = templateOf(id)
  return blocks === 'all' || blocks.includes(type)
}

export const allowedBlockTypes = (id: string | null | undefined): readonly BlockType[] => {
  const { blocks } = templateOf(id)
  return blocks === 'all' ? BLOCK_TYPES : blocks
}

const HexColorSchema = z.union([z.literal(''), z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #6b2733')])

/** Logo, favicon and accent colour overrides. Empty means the template's own. */
export const BrandingSchema = z.object({
  logo: IdSchema.nullable().default(null),
  favicon: IdSchema.nullable().default(null),
  themeColor: HexColorSchema.default(''),
})
export type Branding = z.infer<typeof BrandingSchema>
export const DEFAULT_BRANDING: Branding = { logo: null, favicon: null, themeColor: '' }
